
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import JsonLd from '../../components/seo/JsonLd';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact GCM Mobile Notary",
    "description": "Contact information and appointment booking for GCM Mobile Notary services in Sherman Oaks",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "GCM Mobile Notary",
      "telephone": "+18772113011",
      "email": "office@notaryshermanoaks.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "5103 Longridge Ave",
        "addressLocality": "Sherman Oaks",
        "addressRegion": "CA",
        "postalCode": "91423",
        "addressCountry": "US"
      }
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    location: '',
    message: '',
    urgency: 'standard'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3vqbmkum42jkjthsm2g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          location: '',
          message: '',
          urgency: 'standard'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      info: '(877) 211-3011',
      description: 'Available 7 days a week for appointments',
      action: 'tel:+18772113011'
    },
    {
      icon: 'ri-message-line',
      title: 'Text Us',
      info: '(877) 211-3011',
      description: 'Quick response for urgent requests',
      action: 'sms:+18772113011'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      info: 'office@notaryshermanoaks.com',
      description: 'Send us your questions or documents',
      action: 'mailto:office@notaryshermanoaks.com'
    },
  ];

  const businessHours = [
    { day: 'Monday - Friday', hours: '24 Hours' },
    { day: 'Saturday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: '10:00 AM - 4:00 PM' },
    { day: 'Emergency Service', hours: 'Available 24/7' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact GCM Mobile Notary - Schedule Appointment in Sherman Oaks | (877) 211-3011"
        description="Contact GCM Mobile Notary for professional notarization services in Sherman Oaks. Call (877) 211-3011 to schedule your appointment. Same-day service available throughout San Fernando Valley."
        keywords="Contact Mobile Notary Sherman Oaks, Schedule Notary Appointment, Mobile Notary Phone Number, Sherman Oaks Notary Contact Information"
        canonical={`${siteUrl}/contact`}
        ogTitle="Contact GCM Mobile Notary - Schedule Appointment in Sherman Oaks | (877) 211-3011"
        ogDescription="Contact GCM Mobile Notary for professional notarization services in Sherman Oaks. Call (877) 211-3011 to schedule your appointment. Same-day service available throughout San Fernando Valley."
        ogUrl={`${siteUrl}/contact`}
        ogImage={`${siteUrl}/og-image.jpg`}
      />
      <JsonLd data={contactSchema} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contact GCM Mobile Notary
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Ready to schedule your mobile notary appointment? Contact us today for fast, 
            professional notarization services throughout the San Fernando Valley.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now: (877) 211-3011
            </a>
            <a
              href="mailto:office@notaryshermanoaks.com"
              className="bg-black text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600">
              Multiple ways to reach us for your convenience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${method.icon} text-2xl text-orange-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                <a 
                  href={method.action}
                  className="text-orange-500 font-semibold text-lg hover:text-orange-600 cursor-pointer"
                >
                  {method.info}
                </a>
                <p className="text-gray-600 mt-2">{method.description}</p>
              </div>
            ))}
          </div>

          {/* Office Address */}
          <div className="bg-orange-50 p-8 rounded-xl text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-map-pin-line text-2xl text-orange-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Our Office</h3>
            <p className="text-gray-700 font-semibold text-lg">
              5103 Longridge Ave.<br />
              Sherman Oaks, CA 91423
            </p>
            <p className="text-gray-600 mt-2">Mobile service available throughout the San Fernando Valley</p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Schedule Your Appointment
              </h2>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="(877) 211-3011"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Needed *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select a service</option>
                      <option value="power-of-attorney">Power of Attorney</option>
                      <option value="medical-power-of-attorney">Medical Power of Attorney</option>
                      <option value="living-trust-will">Living Trust &amp; Will</option>
                      <option value="travel-consent">Travel Consent Form</option>
                      <option value="real-estate">Real Estate Documents</option>
                      <option value="apostille">Apostille Service</option>
                      <option value="courier">Courier Service</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency Level
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="standard">Standard (2-4 hours)</option>
                      <option value="same-day">Same Day (within 2 hours)</option>
                      <option value="emergency">Emergency (ASAP)</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Address or general area (e.g., Sherman Oaks, Encino)"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Any specific requirements or questions..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Schedule Appointment'}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800">
                      Thank you! Your appointment request has been submitted. We'll contact you shortly to confirm.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800">
                      Sorry, there was an error submitting your request. Please try calling us directly.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Business Info */}
            <div className="space-y-8">
              {/* Business Hours */}
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Business Hours</h3>
                <div className="space-y-3">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">{schedule.day}</span>
                      <span className="text-gray-600">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                  <p className="text-sm text-teal-800">
                    <i className="ri-information-line mr-2"></i>
                    Emergency and after-hours appointments available with additional fees
                  </p>
                </div>
              </div>

              {/* Service Area */}
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Service Area</h3>
                <p className="text-gray-600 mb-4">
                  Based at 5103 Longridge Ave. in Sherman Oaks, we serve the entire San Fernando Valley including:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div>• Sherman Oaks</div>
                  <div>• Encino</div>
                  <div>• Van Nuys</div>
                  <div>• North Hollywood</div>
                  <div>• Studio City</div>
                  <div>• Woodland Hills</div>
                  <div>• Tarzana</div>
                  <div>• Reseda</div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  And many more areas throughout the valley
                </p>
              </div>

              {/* Quick Contact */}
              <div className="bg-orange-500 text-white p-8 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Need Immediate Service?</h3>
                <p className="mb-6">
                  For urgent notarization needs, call or text us directly for the fastest response.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+18772113011"
                    className="bg-white text-orange-500 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Now
                  </a>
                  <a
                    href="sms:+18772113011"
                    className="bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-message-line mr-2"></i>
                    Text Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Service Area
            </h2>
            <p className="text-xl text-gray-600">
              Sherman Oaks and the entire San Fernando Valley
            </p>
          </div>
          
          <div className="bg-gray-100 rounded-xl p-4">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.8!2d-118.4456!3d34.1508!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bf2a8b8b8b8b%3A0x1234567890abcdef!2s5103%20Longridge%20Ave%2C%20Sherman%20Oaks%2C%20CA%2091423!5e0!3m2!1sen!2sus!4v1635000000000!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
